/* global Engage, jQuery, Elm, _gaq, ga, gtag */

/**
 * @description Initializes the core search module for the Engage Locator.
 * @param  {jQuery} $ - A reference to the global jQuery namespace.
 */
void (function initializeLocatorSearch($) {
	'use strict';

	window.Engage = window.Engage || {};
	Engage.Locator = Engage.Locator || {};

	if (!Engage.Locator.Messaging) {
		const retryDelay = 20;
		setTimeout(() => initializeLocatorSearch($), retryDelay);
		return;
	}

	/**
	 * @namespace Window.Engage.Locator.Search
	 */
	Engage.Locator.Search = Engage.Locator.Search || {};

	/**
	 * @function initialize
	 * @description Initializes and embeds the Engage Locator Search module.
	 * @example
	 * Engage.Locator.Search.initialize(
	 *      <%: this.ModuleId %>,
	 *      document.getElementById('<%: this.LocatorSearch.ClientID%>'));
	 * @memberof Window.Engage.Locator.Search
	 * @param  {number} moduleId - The module Id of the search module.
	 * @param  {element} element - The document element to embed the ELM search application into.
	 */
	Engage.Locator.Search.initialize = (moduleId, element) => {
		const messaging = Engage.Locator.Messaging;
		const servicesFramework = $.ServicesFramework(moduleId); // eslint-disable-line new-cap
		const tabId = servicesFramework.getTabId();
		const antiForgeryValue = servicesFramework.getAntiForgeryValue();
		const serviceRoot = servicesFramework.getServiceRoot('Engage/Locator');
		const options = {
			baseUrl: `${serviceRoot}locations/`,
			headers: {
				ModuleId: moduleId.toString(),
				TabId: tabId,
				RequestVerificationToken: antiForgeryValue,
			},
			localization: Engage.Locator.localResources,
			attributeDefinitions: Engage.Locator.AttributeDefinitions || [],
			showNearMe:
				window.location.protocol === 'https:' &&
				Boolean(navigator && navigator.geolocation) &&
				Boolean(Engage.Locator.SearchResults) &&
				Boolean(Engage.Locator.Settings.ShowNearMeButton),
			settings: Engage.Locator.Settings,
			uniqueId: `${moduleId}-${tabId}`,
			regions: Engage.Locator.regions || [],
			countries: Engage.Locator.countries || [],
			initialAddress: Engage.Locator.initialAddress,
		};
		const elmApp = Elm.Engage.Search.Main.init({
			node: element,
			flags: options,
		});

		/**
		 * @private
		 * @function requestUsersLocation
		 * @description Requests the user's location from the browser.
		 * @memberof Window.Engage.Locator.Search
		 */
		function requestUsersLocation() {
			if (!navigator || !navigator.geolocation) {
				return;
			}

			const geolocationOptions = {
				timeout: 10000,
			};
			const onGeolocationSuccess = ({ coords }) => {
				elmApp.ports.sub.send({
					type: 'ReceiveUserLocationResult',
					data: {
						name: 'UserLocation',
						latitude: coords.latitude,
						longitude: coords.longitude,
					},
				});
			};
			const onGeolocationError = ({ code }) => {
				elmApp.ports.sub.send({
					type: 'ReceiveUserLocationResult',
					data: {
						errorCode: code,
					},
				});
			};

			navigator.geolocation.getCurrentPosition(
				onGeolocationSuccess,
				onGeolocationError,
				geolocationOptions,
			);
		}

		/**
		 * @private
		 * @function trackEvent
		 * @description Tracks an event with Google Analytics
		 * @param {Object} payload - Event information
		 * @memberof Window.Engage.Locator.Search
		 */
		function trackEvent(payload) {
			if (window._gaq) {
				_gaq.push([
					'_trackEvent',
					payload.eventCategory,
					payload.eventAction,
					payload.eventLabel,
					payload.eventValue,
					payload.noninteraction || false,
				]);
			}

			if (window.ga) {
				ga('send', 'event', {
					eventCategory: payload.eventCategory,
					eventAction: payload.eventAction,
					eventLabel: payload.eventLabel,
					eventValue: payload.eventValue,
					nonInteraction: payload.noninteraction || false,
				});
			}

			if (window.gtag) {
				gtag('event', payload.eventAction, {
					event_category: payload.eventCategory,
					event_label: payload.eventLabel,
					value: payload.eventValue,
					non_interaction: payload.noninteraction || false,
				});
			}
		}

		/**
		 * @private
		 * @name elmOutboundPortCallback
		 * @typedef elmPortCallback
		 * @description Handles all outgoing messages from the Engage Locator Search ELM application.
		 * @memberof Window.Engage.Locator.Search
		 * @param {Object} payload - The JSON payload sent from ELM.
		 */
		elmApp.ports.out.subscribe((payload) => {
			if (!payload || !payload.type) {
				return;
			}

			switch (payload.type.toUpperCase()) {
				case 'VALIDATEZIPCODEREQUEST':
					messaging.publish(
						messaging.messageTypes.validateZipCode,
						payload.data,
					);
					break;
				case 'ADVANCEDSEARCHREQUEST':
					messaging.publish(
						messaging.messageTypes.advancedSearch,
						payload.data,
					);
					break;
				case 'LOCATIONUPDATE':
					messaging.publish(
						messaging.messageTypes.locationUpdate,
						payload.data,
					);
					break;
				case 'USERSLOCATIONREQUEST':
					requestUsersLocation();
					break;
				case 'TRACKEVENT':
					trackEvent(payload.data);
					break;
				case 'RESETSEARCHFORM':
					messaging.publish(
						messaging.messageTypes.resetSearchFrom,
						payload.data,
					);
					break;
				case 'NEWQUERYFILTERS':
					messaging.publish(
						messaging.messageTypes.newQueryFilters,
						payload.data,
					);
					break;
				case 'RESETCUSTOMFILTERS':
					messaging.publish(
						messaging.messageTypes.resetCustomFilters,
						payload.data,
					);
					break;
				default:
					return;
			}
		});

		/**
		 * @private
		 * @name trackEventSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.trackEvent} messages.
		 * @memberof Window.Engage.Locator.Search
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.trackEvent,
			(messageType, payload) => {
				if (
					messageType !== messaging.messageTypes.trackEvent ||
					!payload
				) {
					return;
				}

				trackEvent(payload);
			},
		);

		/**
		 * @private
		 * @name zipCodeValidationResultSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Handles all {Window.Engage.Locator.Messaging.messageTypes.zipCodeValidationResult} messages.
		 * @memberof Window.Engage.Locator.Search
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.messageTypes.zipCodeValidationResult,
			(messageType, payload) => {
				if (
					messageType !==
						messaging.messageTypes.zipCodeValidationResult ||
					!payload
				) {
					return;
				}

				const type = 'ZipCodeValidationResult';
				const payloadData = payload.data || {};
				const searchedAddress = payloadData.address;
				switch (payload.status.toUpperCase()) {
					case 'ERROR':
						elmApp.ports.sub.send({
							type,
							data: { message: payload.error },
							address: searchedAddress,
						});
						break;
					case 'NO_MATCHES_FOUND':
						elmApp.ports.sub.send({
							type,
							data: payloadData,
							address: searchedAddress,
						});
						break;
					case 'EXACT_MATCH_FOUND':
					case 'MULTIPLE_MATCHES_FOUND':
						elmApp.ports.sub.send({
							type,
							data: payloadData,
							address: searchedAddress,
						});
						break;
					default:
						return;
				}
			},
		);

		/**
		 * @private
		 * @const {string[]} messagesThatTriggerSearch - An array of all messages types that trigger a search event.
		 * @memberof Window.Engage.Locator.Search
		 */
		const messagesThatTriggerSearch = [
			messaging.messageTypes.validateZipCode,
			messaging.messageTypes.locationUpdate,
			messaging.messageTypes.newQueryFilters,
			messaging.messageTypes.resetSearchFrom,
			messaging.messageTypes.boundingBoxUpdate,
			messaging.messageTypes.showLoading,
			messaging.messageTypes.advancedSearch,
		];

		/**
		 * @private
		 * @name messagesThatTriggerSearchSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Subscribe to all messages in the Engage Locator, only handle messages
		 * that exist in the {messagesThatTriggerSearch} member.
		 * @memberof Window.Engage.Locator.Search
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(
			messaging.locatorMessagePrefix,
			(messageType, payload) => {
				if (messagesThatTriggerSearch.indexOf(messageType) === -1) {
					return;
				}

				if (
					messageType === messaging.messageTypes.validateZipCode &&
					Boolean(Engage.Locator.Settings.SearchResultsUrl) &&
					Boolean(payload && payload.address)
				) {
					window.location.href = `${Engage.Locator.Settings.SearchResultsUrl}?address=${payload.address}`;
				}

				if (messageType === messaging.messageTypes.advancedSearch) {
					window.location.href = `${Engage.Locator.Settings.SearchResultsUrl}?advanced=true`;
				}

				elmApp.ports.sub.send({
					type: 'PerformSearchResult',
				});
			},
		);

		/**
		 * @private
		 * @const {string[]} messagesThatMarkSearchAsComplete - An array of all messages types that mark a search
		 * action has been completed.
		 * @memberof Window.Engage.Locator.Search
		 */
		const messagesThatMarkSearchAsComplete = [
			messaging.messageTypes.zipCodeValidationResult,
			messaging.messageTypes.newLocations,
			messaging.messageTypes.advancedSearch,
		];

		/**
		 * @private
		 * @name messagesThatMarkSearchAsCompleteSubscriptionCallback
		 * @typedef subscriptionCallback
		 * @description Subscribe to all messages in the Engage Locator, only handle messages
		 * that exist in the {messagesThatMarkSearchAsComplete} member.
		 * @memberof Window.Engage.Locator.Search
		 * @param {string} messageType - The type of message requesting publish.
		 * @param {Object} payload - The object payload for the message requesting publish.
		 */
		messaging.subscribe(messaging.locatorMessagePrefix, (messageType) => {
			if (messagesThatMarkSearchAsComplete.indexOf(messageType) === -1) {
				return;
			}

			elmApp.ports.sub.send({
				type: 'SearchCompletedResult',
			});
		});
	};
})(jQuery);
